import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { i18n } from "../../../../../../i18n/i18nText";
import moment from "moment";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  coreHTTPClient,
  getAuthorization,
} from "../../../../../../services/webclient";
import FormSelect from "../../../../../../components/Form/FormSelect/FormSelect";
import Chart from "react-apexcharts";
import DashboardBox from "../../../../components/DashboardBox/DashboardBox";
import { isMobile } from "../../../../../../mobileConfig";
import GeneralChartDateSelect from "../../../../components/ChartDateSelect/GeneralChartDateSelect";
import ChartPagination from "../../../../components/ChartPagination/ChartPagination";
import { Button } from "@material-ui/core";
import { GetApp } from "@material-ui/icons";
import axios from "axios";
import useNotify from "../../../../../../hooks/tools/useNotify";
import { formatFloatValue } from "../../../../../../utils/models/format";

const Container = styled.div`
  padding-bottom: 15px;
  width: 100%;
  height: 100%;
`;

const TotalRainfall = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin-bottom: 15px;
  font-size: 15.75px;
  font-weight: 500;
`;

type DataObject = { from: Date; to: Date; value: number };

interface Props {
  farmID: number;
  pivotID: number;
}

function PluviometerChart(props: Props) {
  const { farmID, pivotID } = props;

  const [chartPage, setChartPage] = useState(0);
  const [lastPage, setLastPage] = useState(0);
  const [chartDataLength, setChartDataLength] = useState("12");
  const [chartPrecision, setChartPrecision] = useState("1");
  const [tempStartDate, setTempStartDate] = useState<moment.Moment>(
    moment().subtract(1, "month")
  );
  const [tempEndDate, setTempEndDate] = useState<moment.Moment>(moment());
  const [startDate, setStartDate] = useState<moment.Moment>(
    moment().subtract(1, "month")
  );
  const [endDate, setEndDate] = useState<moment.Moment>(moment());
  const [chartData, setChartData] = useState<DataObject[]>();

  let filesaver = require("file-saver");
  const notify = useNotify();
  const [sendingExcel, setSendingExcel] = useState<boolean>(false);

  const handleExcelClick = (): void => {
    setSendingExcel(true);
    if (isMobile()) {
      setSendingExcel(false);
      window.open(
        `${
          process.env.WEB_URL
        }/v3/farms/${farmID}/pivots/${pivotID}/pluviometer/report/${chartPrecision}/?date_start=${
          chartPrecision !== "30" ? startDate.format("YYYY-M-D") : "YYYY-M"
        }&date_end=${endDate.format(
          chartPrecision !== "30" ? "YYYY-M-D" : "YYYY-M"
        )}`
      );
    } else {
      axios
        .get(
          `/v3/farms/${farmID}/pivots/${pivotID}/pluviometer/report/${chartPrecision}/?date_start=${startDate.format(
            chartPrecision !== "30" ? "YYYY-M-D" : "YYYY-M"
          )}&date_end=${endDate.format(
            chartPrecision !== "30" ? "YYYY-M-D" : "YYYY-M"
          )}`,
          {
            headers: {
              Authorization: `Bearer ${getAuthorization()}`,
              "Content-Type":
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
              Connection: "close",
            },
            baseURL: process.env.REACTWEB_COREAPI_URL,
            responseType: "blob",
          }
        )
        .then((response) => {
          let blob: Blob = new Blob([response.data], {
            type:
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          filesaver.saveAs(blob, `pluviometro-pivot${pivotID}.csv`);
        })
        .catch((err) => {
          notify("PIVOT_REPORT_EXCEL_ERROR", "error");
        })
        .finally(() => {
          setSendingExcel(false);
        });
    }
  };

  const excelButton = () => {
    return (
      <Button
        className="excel-button"
        size="small"
        onClick={handleExcelClick}
        startIcon={sendingExcel ? <CircularProgress size={18} /> : <GetApp />}
        disabled={sendingExcel}
      >
        <>{i18n("PIVOT_REPORT_EXCEL")}</>
      </Button>
    );
  };

  function handleDataLength(value) {
    setChartDataLength(value);
    setChartPage(0);
  }

  function handleChartPrecision(value) {
    setChartPrecision(value);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }

  async function getRainfallData() {
    let startDateURL: string;
    let endDateURL: string;

    if (chartPrecision == "30") {
      startDateURL = startDate.format("YYYY-MM");
      endDateURL = endDate.format("YYYY-MM");
    } else {
      startDateURL = startDate.format("YYYY-M-D");
      endDateURL = endDate.format("YYYY-M-D");
    }

    try {
      const response = await coreHTTPClient.get(
        `v3/farms/${farmID}/pivots/${pivotID}/histogram/${chartPrecision}/?date_start=${startDateURL}&date_end=${endDateURL}`
      );
      if (response.data) {
        setChartData(response.data);
        setLastPage(
          Math.ceil(response.data?.length / parseInt(chartDataLength, 10))
        );
        setChartPage(
          Math.ceil(response.data?.length / parseInt(chartDataLength, 10)) - 1
        );
      } else {
        setLastPage(0);
        setChartPage(0);
      }
    } catch (err) {
      console.log(err);
    }
  }

  function MyResponsiveBar(chartData: DataObject[]) {
    const sortedData = chartData
      .map((chartDatum) => ({
        labelDate:
          chartPrecision == "30"
            ? moment(chartDatum.from).format("MMM")
            : moment(chartDatum.from),
        valueColor: "#68c0f3",
        value: chartDatum.value ? chartDatum.value.toFixed(2) : 0,
        from: chartDatum.from,
        to: chartDatum.to,
      }))
      .sort((a, b) => {
        if (moment(a.from).isBefore(b.from)) {
          return -1;
        }
        if (moment(a.from).isAfter(b.from)) {
          return 1;
        }
        return 0;
      })
      .slice(
        parseInt(chartDataLength, 10) * chartPage,
        parseInt(chartDataLength, 10) * (chartPage + 1)
      );

    const state = {
      series: [
        {
          name: "mm",
          data: sortedData.map((sortedDatum) => sortedDatum?.value),
        },
      ],
      options: {
        chart: {
          toolbar: {
            show: false,
          },
          events: {
            mounted: () => {
              window.scrollTo(0, 0);
            },
          },
        },
        colors: ["#68c0f3"],
        grid: {
          row: {
            colors: ["#fff", "#f7f2f2"],
          },
        },
        dataLabels: {
          enabled: true,
          enabledOnSeries: undefined,
          formatter: function (value) {
            return formatFloatValue(value);
          },
          style: {
            colors: ["rgba(51, 51, 51, 0.8)"],
            fontSize: "11px",
          },
        },
        xaxis: {
          categories: sortedData.map((sortedDatum) =>
            chartPrecision == "1" && sortedDatum.from != sortedDatum.to
              ? `${moment(sortedDatum.from).format("DD MMM")} - ${moment(
                  sortedDatum.to
                ).format("DD MMM")}`
              : chartPrecision == "7"
              ? `${moment(sortedDatum.labelDate).format("DD MMM")} 
            - ${moment(sortedDatum.labelDate).add(6, "day").format("DD MMM")}`
              : chartPrecision == "30"
              ? sortedDatum.labelDate
              : moment(sortedDatum.labelDate).format("DD MMM")
          ),
          labels: {
            rotate: -45,
          },
        },
        yaxis: {
          labels: {
            formatter(value) {
              return value.toFixed();
            },
          },
        },
        title: {
          text: `${i18n("BASIC_INFO_BOX_RAINFALL")} (mm)`,
          align: "center",
          offsetY: 20,
        },
        tooltip: {
          y: {
            formatter(value) {
              return formatFloatValue(value);
            },
          },
        },
      },
    };
    return (
      <Chart
        id="PluviometerChart"
        // @ts-ignore
        options={state.options}
        series={state.series}
        type="bar"
        width={isMobile() ? "100%" : "180%"}
        height={isMobile() ? "350px" : "400px"}
      />
    );
  }

  const totalRainfall = useMemo(() => {
    let totalRainfall = 0;
    if (chartData?.length) {
      chartData.forEach((chartDatum) => {
        totalRainfall = totalRainfall + chartDatum.value;
      });
    }
    return totalRainfall;
  }, [chartData]);

  useEffect(() => {
    getRainfallData();
  }, [chartPrecision, chartDataLength, startDate, endDate, pivotID]);

  return (
    <DashboardBox
      centerElement={<h2>{i18n("BASIC_INFO_BOX_CHART_PLUVIOMETER")}</h2>}
      leftElement={excelButton()}
      rightElement={
        <GeneralChartDateSelect
          chartPrecision={chartPrecision}
          tempStartDate={tempStartDate}
          tempEndDate={tempEndDate}
          setTempStartDate={setTempStartDate}
          setTempEndDate={setTempEndDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      }
    >
      <Container>
        <div className="basic-info__header-infos">
          {i18n("BASIC_INFO_BOX_CHART_GENERATED_BETWEEN", [
            moment(startDate).format("DD MMM YYYY"),
            moment(endDate).format("DD MMM YYYY"),
          ])}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <div
            style={{
              margin: "15px 20px",
              width: "100%",
              maxWidth: "300px",
            }}
          >
            <FormSelect
              id="SelectPrecision"
              newLabel="SCALE"
              state={[chartPrecision, handleChartPrecision]}
              values={[
                ["1", "EDIT_FARM_DAY"],
                ["7", "EDIT_FARM_WEEK"],
                ["30", "EDIT_FARM_MONTH"],
              ]}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div>
            {chartData ? (
              chartData.length > 0 ? (
                <>
                  {MyResponsiveBar(chartData)}

                  <TotalRainfall>
                    {`${i18n("PERIOD_TOTAL_RAINFALL")}: ${formatFloatValue(
                      totalRainfall
                    )} mm`}
                  </TotalRainfall>
                </>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: isMobile() ? "380px" : "430px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {i18n("METERSYSTEM_NO_READINGS")}
                </div>
              )
            ) : (
              <div
                style={{
                  width: "100%",
                  height: isMobile() ? "380px" : "430px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <CircularProgress />
              </div>
            )}
          </div>
        </div>

        <ChartPagination
          lastPage={lastPage}
          chartPage={chartPage}
          setChartPage={setChartPage}
          chartDataLength={chartDataLength}
          chartData={chartData}
        />
      </Container>
    </DashboardBox>
  );
}

export default PluviometerChart;
